import { useState, useEffect, useCallback, useRef } from "react";
import { authFetch, API_BASE } from "../api";

// Poll interval for the Apps list. Older code used 5s which caused ~N+1 requests
// every 5 seconds (one list + one manifest per app) — serious backend thrash
// once a user had ~30 apps. We now (a) bump this to 30s, and (b) fold version
// info into the /integrations response itself (backend change), collapsing the
// per-cycle cost to a single HTTP call.
const POLL_INTERVAL_MS = 30000;

export default function useIntegrations(enabled = true) {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const visibleRef = useRef(true);

  const fetchIntegrations = useCallback(async () => {
    if (!visibleRef.current) return;
    try {
      // Single request — /api/integrations now returns version/publishedAt
      // inline, so no per-app manifest fan-out is needed.
      const res = await authFetch(`${API_BASE}/integrations`);
      const data = await res.json();
      setIntegrations(data.integrations || []);
    } catch (err) {
      console.error("Failed to fetch integrations:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    fetchIntegrations();
    const interval = setInterval(fetchIntegrations, POLL_INTERVAL_MS);

    const handleVisibility = () => {
      visibleRef.current = document.visibilityState === "visible";
      if (visibleRef.current) fetchIntegrations();
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [enabled, fetchIntegrations]);

  const doAction = useCallback(async (name, action, method = "POST") => {
    setActionLoading(`${name}-${action}`);
    try {
      const url =
        action === "remove"
          ? `${API_BASE}/integrations/${name}`
          : `${API_BASE}/integrations/${name}/${action}`;
      await authFetch(url, { method });
      await fetchIntegrations();
    } catch (err) {
      console.error(`${action} failed:`, err);
    } finally {
      setActionLoading(null);
    }
  }, [fetchIntegrations]);

  return { integrations, loading, actionLoading, doAction, fetchIntegrations };
}
